import { Phone, Mail, MapPin, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE, NAV } from "../lib/site";
import { POSTS } from "../lib/blog";
import { useT } from "../lib/i18n";

const CITY_LINKS = [
  { label: "Guéret", href: "/paysagiste-gueret" },
  { label: "La Souterraine", href: "/paysagiste-la-souterraine" },
  { label: "Aubusson", href: "/paysagiste-aubusson" },
  { label: "Bourganeuf", href: "/paysagiste-bourganeuf" },
  { label: "Boussac", href: "/paysagiste-boussac" },
  { label: "La Celle-Dunoise", href: "/paysagiste-la-celle-dunoise" },
];

export default function Footer() {
  const { t } = useT();
  const serviceLinks = t("footer.serviceLinks");
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0F2E11] text-white pt-20 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={SITE.logo}
                alt={`Logo ${SITE.name} — paysagiste et anti-nuisibles en Creuse`}
                width="56"
                height="56"
                loading="lazy"
                decoding="async"
                className="h-14 w-14 rounded-full object-cover"
              />
              <div>
                <div className="font-['Outfit'] text-xl font-semibold">
                  K'REUSE EXTÉRIEUR
                </div>
                <div className="text-sm text-[#A5D6A7]">
                  {t("footer.tagline")}
                </div>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed max-w-md">
              {t("footer.intro")}
            </p>
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-[#1877F2] hover:bg-[#0e63d6] text-white text-sm font-medium transition-colors"
            >
              <Facebook className="w-4 h-4" fill="currentColor" strokeWidth={0} />
              {t("footer.facebook")}
            </a>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-['Outfit'] font-semibold text-[#A5D6A7] uppercase text-xs tracking-widest mb-5">
              {t("footer.navTitle")}
            </h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a
                    href={n.href.startsWith("/") ? n.href : `/${n.href}`}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    {t(`nav.${n.label}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <h4 className="font-['Outfit'] font-semibold text-[#A5D6A7] uppercase text-xs tracking-widest mb-5">
              {t("footer.servicesTitle")}
            </h4>
            <ul className="space-y-3 mb-8">
              {serviceLinks.map((s) => (
                <li key={s.href}>
                  <Link
                    to={s.href}
                    className="text-white/80 hover:text-white transition-colors text-sm"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="font-['Outfit'] font-semibold text-[#A5D6A7] uppercase text-xs tracking-widest mb-5">
              {t("footer.citiesTitle")}
            </h4>
            <ul className="grid grid-cols-2 gap-2 mb-8">
              {CITY_LINKS.map((c) => (
                <li key={c.href}>
                  <Link
                    to={c.href}
                    className="text-white/80 hover:text-white transition-colors text-sm"
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
              <li className="col-span-2 mt-2">
                <Link
                  to="/zone-intervention-creuse-et-limitrophes"
                  data-testid="footer-zone-intervention"
                  className="text-[#A5D6A7] hover:text-white font-medium transition-colors text-sm"
                >
                  + Départements limitrophes (87, 36, 03) →
                </Link>
              </li>
              <li className="col-span-2 mt-1">
                <Link
                  to="/entreprise-nuisibles-creuse"
                  data-testid="footer-pest-pillar"
                  className="text-[#A5D6A7] hover:text-white font-medium transition-colors text-sm"
                >
                  → Anti-nuisibles Creuse (page dédiée)
                </Link>
              </li>
              <li className="col-span-2 mt-1">
                <Link
                  to="/zone-intervention-creuse-et-limitrophes#communes"
                  data-testid="footer-pest-cities"
                  className="text-[#A5D6A7] hover:text-white font-medium transition-colors text-sm"
                >
                  → Anti-nuisibles par commune (27 villes)
                </Link>
              </li>
              <li className="col-span-2 mt-1">
                <Link
                  to="/meilleure-entreprise-anti-nuisibles-creuse"
                  data-testid="footer-choose-pillar"
                  className="text-[#A5D6A7] hover:text-white font-medium transition-colors text-sm"
                >
                  → Comment choisir son entreprise
                </Link>
              </li>
              <li className="col-span-2 mt-1">
                <Link
                  to="/traitement-vrillettes-bois-creuse"
                  data-testid="footer-vrillettes-pillar"
                  className="text-[#A5D6A7] hover:text-white font-medium transition-colors text-sm"
                >
                  → Traitement vrillettes du bois
                </Link>
              </li>
              <li className="col-span-2 mt-1">
                <Link
                  to="/a-propos-kevin-fondateur"
                  data-testid="footer-about-kevin"
                  className="text-[#A5D6A7] hover:text-white font-medium transition-colors text-sm"
                >
                  → À propos de Kevin, fondateur
                </Link>
              </li>
            </ul>

            <h4 className="font-['Outfit'] font-semibold text-[#A5D6A7] uppercase text-xs tracking-widest mb-5">
              {t("footer.blogTitle")}
            </h4>
            <ul className="space-y-3 mb-8">
              <li>
                <Link
                  to="/conseils"
                  className="text-white font-medium hover:text-[#A5D6A7] transition-colors text-sm"
                >
                  {t("footer.blogAll")}
                </Link>
              </li>
              {POSTS.slice(0, 3).map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/conseils/${p.slug}`}
                    className="text-white/80 hover:text-white transition-colors text-sm"
                  >
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="font-['Outfit'] font-semibold text-[#A5D6A7] uppercase text-xs tracking-widest mb-5">
              {t("footer.contactTitle")}
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${SITE.phoneRaw}`}
                  className="flex items-center gap-3 text-white/80 hover:text-white text-sm"
                >
                  <Phone className="w-4 h-4 text-[#A5D6A7]" />
                  {SITE.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${SITE.email}`}
                  className="flex items-center gap-3 text-white/80 hover:text-white break-all text-sm"
                >
                  <Mail className="w-4 h-4 text-[#A5D6A7]" />
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/80 text-sm">
                <MapPin className="w-4 h-4 text-[#A5D6A7]" />
                {t("footer.zone")}
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col gap-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70 justify-center md:justify-start">
            <Link to="/mentions-legales" className="hover:text-white" data-testid="footer-mentions">
              {t("footer.mentions")}
            </Link>
            <Link to="/politique-confidentialite" className="hover:text-white" data-testid="footer-rgpd">
              {t("footer.rgpd")}
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/50">
            <div>
              © {new Date().getFullYear()} K'REUSE EXTÉRIEUR. {t("footer.rights")}
            </div>
            <div>{t("footer.made")}</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
